import React, {useEffect, useState} from 'react';
import {useGetWorkspaceProductsQuery, useGetWorkspaceQuery} from "../../store/Api";
import {Icon, Spinner, SpinnerSize, Stack, Text, TooltipHost} from "@fluentui/react";
import CurrencyFormat from "react-currency-format";
import {useCalculatePriceEstimateMutation} from "../../store/endpoints/PreparedCartEndpoints";

interface IWorkspaceTotalsDisplayProps {
    workspace: string;
}

const WorkspaceTotalsDisplay: React.FC<IWorkspaceTotalsDisplayProps> = (props) => {
    const [getEstimate] = useCalculatePriceEstimateMutation();
    const products = useGetWorkspaceProductsQuery(props.workspace, {skip: !props.workspace || props.workspace.length === 0});
    const workspace = useGetWorkspaceQuery(props.workspace, {skip:!props.workspace || props.workspace.trim().length === 0});
    const [cost, setCost] = useState<number | undefined>(undefined);
    const [price, setPrice] = useState<number | undefined>(undefined);
    const [isEstimating, setIsEstimating] = useState(false);
    // Rapid edits (removing several lines) fire overlapping estimates; only the newest may win.
    const estimateSequence = React.useRef(0);
    const [messages, setMessages] = useState<{[key: string]: string}>({});
    const [estimateFailed, setEstimateFailed] = useState(false);

    useEffect(() => {
        if (products.data && products.data.length === 0) {
            // Nothing left to price: clear the previous numbers instead of showing the last estimate.
            estimateSequence.current += 1;
            setCost(undefined);
            setPrice(undefined);
            setMessages({});
            setEstimateFailed(false);
            setIsEstimating(false);
            return;
        }
        if(workspace.data && products.data && products.data.length > 0 && props.workspace){
            const sequence = ++estimateSequence.current;
            setIsEstimating(true);
            setEstimateFailed(false);
            const result = getEstimate({workspaceId: props.workspace, items: products.data}).unwrap();
            result.then((res) => {
                if (sequence !== estimateSequence.current) return;
                if(res) {
                    const hasMessages = Object.keys(res.messages ?? {}).length > 0;
                    setMessages(res.messages ?? {});
                    if(!hasMessages){
                        setCost(res.cost);
                        setPrice(res.price);
                    }
                    else{
                        setCost(undefined);
                        setPrice(undefined);
                    }
                }
            }).catch(() => {
                // A failed estimate reads as "not available" rather than escaping as an unhandled rejection.
                if (sequence !== estimateSequence.current) return;
                setCost(undefined);
                setPrice(undefined);
                setMessages({});
                setEstimateFailed(true);
            }).finally(() => { if (sequence === estimateSequence.current) setIsEstimating(false); });
        }
    }, [workspace.data, products.data]);

    const formatCurrency = (amount?: number) => {
        if (amount === null || amount === undefined) return null;
        return <CurrencyFormat
            value={amount}
            displayType="text"
            thousandSeparator=","
            prefix="$"
            decimalScale={2}
            fixedDecimalScale={true}
        />;
    }

    const getPriceEstimateMessages = () => {
        if (!messages || Object.keys(messages).length === 0){
            return null;
        }

        const content = (
            <React.Fragment>
                {Object.entries(messages).map(([key, message]) => {
                    return (
                        <div key={key} style={{fontSize: '90%' }}>
                            {getItemNameFromKey(key)} - {message}
                        </div>
                    )}
                )}
            </React.Fragment>
        );
        return (<TooltipHost
                content={content}
                calloutProps={{ gapSpace: 0 }}
                styles={{ root: { display: 'inline-block', marginLeft: '8px' } }}
            >
                <div>
                    <Icon iconName='Warning' style={{ color: '#ff0000', cursor: 'pointer' }} />
                    Product Sourcing Errors
                </div>
            </TooltipHost>
        )

    }
    const getItemNameFromKey = (key: string) => {
        const item = products?.data?.find(a => a.productId === key);
        if(item){
            return item.product?.name
        }
        const itemByWorkspaceId = products?.data?.find(a => a.id === key);
        if(itemByWorkspaceId){
            return itemByWorkspaceId.product?.name
        }

        if(key.endsWith('_BUNDLE')) {
            const bundleItem = products?.data?.find(b => b.sourceEntityId === key.replace('_BUNDLE', '') && b.isBundle);
            return bundleItem?.adhoc?.name ?? key;
        }
        return key;
    }

    const hasMessages = Object.keys(messages).length > 0;

    return <Stack style={{minHeight: '50px'}} tokens={{childrenGap: '0.5em'}} horizontalAlign={"start"} verticalAlign="center">
        {isEstimating && <Spinner size={SpinnerSize.small} label="Estimating..." />}
        {!isEstimating && <>
            {hasMessages && <span>{getPriceEstimateMessages()}</span>}
            {(cost === undefined || cost === null) && (price === undefined || price === null) && !hasMessages
                ? (estimateFailed ? <span>Price estimate not available</span> : null)
                : (cost === undefined || cost === null) && (price === undefined || price === null)
                    ? <span>Price estimate not available</span>
                    : <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <Text variant='mediumPlus'>Total Cost: {formatCurrency(cost)}</Text>
                        <Text variant='mediumPlus'>Total Price: {formatCurrency(price)}</Text>
                      </Stack>
            }
        </>}
    </Stack>
}

export default WorkspaceTotalsDisplay;